import { getCurrentOrganization as getLegacyAmbientOrganization } from '@/utils/helpers';
import { ORGANIZATION_HEADER } from './axiosInterceptors';
import { validateTenantScope, type AuthPolicy, type TenantScope } from './identity';
import {
  isOperationRequirementCurrent,
  markOperationRequirementTransportEntered,
  operationRequirementSignal,
  SessionTransitionError,
  type OperationRequirement,
} from './operation';
import { getCommittedOrganization, getOrganizationGeneration, getOrganizationSignal, hasCommittedOrganizationIdentity } from './organization';
import { emitSessionTelemetry } from './telemetry';
import { getAccessToken, getAuthGeneration, getSessionAuthSignal, getSessionSubject } from './token';
import { isAdminTargetCapability, type AdminTargetCapability } from './adminTargetCapability';

export type SessionFetchOptions = {
  // Explicit tenant scope for application callers. When absent, the supported
  // browser-console/compatibility path derives the header from the live URL.
  // Application endpoint surfaces must always provide this option.
  organizationScope?: TenantScope;
  authPolicy?: AuthPolicy;
  operationRequirement?: OperationRequirement;
  semanticWrite?: boolean;
  // Internal marker for the audited read-like POST transport wrappers.
  _sessionSemanticRead?: true;
  _sessionAdminTargetCapability?: AdminTargetCapability;
  allowAmbientConsoleScope?: boolean;
  signal?: AbortSignal;
};

const AUTHORIZATION_HEADER = 'Authorization';

const combineSignals = (signals: Array<AbortSignal | null | undefined>): AbortSignal | undefined => {
  const active = signals.filter((signal): signal is AbortSignal => Boolean(signal));
  if (active.length === 0) return undefined;
  if (active.length === 1) return active[0];
  return AbortSignal.any(active);
};

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { name?: unknown; code?: unknown };
  return candidate.name === 'AbortError' || candidate.code === 'ABORT_ERR';
};

type SessionResponseGuard = {
  isCurrent(): boolean;
  callerSignals: readonly AbortSignal[];
  sessionSignal?: AbortSignal;
  telemetryPath: string;
  stalePublicationTelemetryEmitted: boolean;
};

// A guard follows cloned and normalized Response instances so a scope switch
// between receiving headers and consuming a large body is still discarded.
const sessionResponseGuards = new WeakMap<Response, SessionResponseGuard>();

const throwIfCallerAborted = (guard: SessionResponseGuard): void => {
  if (!guard.callerSignals.some((signal) => signal.aborted)) return;
  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  throw error;
};

const stalePublicationTransition = (guard: SessionResponseGuard): SessionTransitionError => {
  if (!guard.stalePublicationTelemetryEmitted) {
    guard.stalePublicationTelemetryEmitted = true;
    emitSessionTelemetry({ code: 'stale_publication_suppressed', transport: 'fetch', path: guard.telemetryPath });
  }
  return new SessionTransitionError();
};

const assertGuardCurrent = (guard: SessionResponseGuard): void => {
  throwIfCallerAborted(guard);
  if (!guard.isCurrent()) throw stalePublicationTransition(guard);
};

const assertResponseGuardCurrent = (response: Response): void => {
  const guard = sessionResponseGuards.get(response);
  if (guard) assertGuardCurrent(guard);
};

const mapBodyReadCancellation = async <T>(response: Response, read: () => Promise<T>): Promise<T> => {
  assertResponseGuardCurrent(response);
  try {
    const result = await read();
    assertResponseGuardCurrent(response);
    return result;
  } catch (error) {
    const guard = sessionResponseGuards.get(response);
    if (guard?.callerSignals.some((signal) => signal.aborted) && isAbortError(error)) throw error;
    if (guard?.sessionSignal?.aborted && isAbortError(error)) throw stalePublicationTransition(guard);
    throw error;
  }
};

const wrapBodyReaders = (response: Response, guard: SessionResponseGuard): Response => {
  if (sessionResponseGuards.has(response)) return response;

  sessionResponseGuards.set(response, guard);
  const arrayBuffer = response.arrayBuffer.bind(response);
  const blob = response.blob.bind(response);
  const formData = response.formData.bind(response);
  const json = response.json.bind(response);
  const text = response.text.bind(response);
  const clone = response.clone.bind(response);

  response.arrayBuffer = () => mapBodyReadCancellation(response, arrayBuffer);
  response.blob = () => mapBodyReadCancellation(response, blob);
  response.formData = () => mapBodyReadCancellation(response, formData);
  response.json = () => mapBodyReadCancellation(response, json);
  response.text = () => mapBodyReadCancellation(response, text);
  response.clone = () => {
    assertResponseGuardCurrent(response);
    return inheritSessionResponseGuard(response, clone());
  };
  return response;
};

export const inheritSessionResponseGuard = (source: Response, target: Response): Response => {
  const guard = sessionResponseGuards.get(source);
  return guard ? wrapBodyReaders(target, guard) : target;
};

export const assertSessionResponseCurrent = (response: Response): void => {
  assertResponseGuardCurrent(response);
};

// The fetch-side transport for window.api and migrated streaming callers. No
// 401 refresh/replay exists on this path (it never did), but auth/scope
// liveness and response-body guards match the Axios transport. Callers own
// retries and visible publication gates.
export const sessionFetch = async (input: RequestInfo | URL, init: RequestInit = {}, options: SessionFetchOptions = {}): Promise<Response> => {
  const inputRequest = typeof Request !== 'undefined' && input instanceof Request ? input : null;
  const telemetryPath = inputRequest?.url ?? String(input);
  const headers = new Headers(init.headers === undefined ? inputRequest?.headers : init.headers);

  const dispatchSubject = getSessionSubject();
  const dispatchAuthGeneration = getAuthGeneration();
  const dispatchAuthSignal = getSessionAuthSignal();
  const authPolicy = options.authPolicy ?? 'session';

  if (authPolicy === 'anonymous') {
    headers.delete(AUTHORIZATION_HEADER);
  } else if (!headers.has(AUTHORIZATION_HEADER)) {
    const token = getAccessToken();
    if (token) headers.set(AUTHORIZATION_HEADER, `Bearer ${token}`);
  }

  let scope: TenantScope | undefined;
  if (options.organizationScope !== undefined) {
    try {
      scope = validateTenantScope(options.organizationScope);
    } catch {
      emitSessionTelemetry({ code: 'organization_scope_invalid', transport: 'fetch', path: telemetryPath });
      throw new SessionTransitionError();
    }
  }
  if (scope) {
    if (scope.kind === 'organization') {
      headers.set(ORGANIZATION_HEADER, scope.organization);
    } else {
      headers.delete(ORGANIZATION_HEADER);
    }
  } else if (options.allowAmbientConsoleScope && !headers.has(ORGANIZATION_HEADER)) {
    // Supported browser-console/compatibility behavior: derive the tenant
    // from the live URL when the caller deliberately omitted explicit scope.
    const ambient = typeof window === 'undefined' ? '' : getLegacyAmbientOrganization();
    if (ambient) {
      headers.set(ORGANIZATION_HEADER, ambient);
      emitSessionTelemetry({ code: 'legacy_ambient_scope_used', transport: 'fetch', path: telemetryPath });
    }
  } else if (!scope) {
    emitSessionTelemetry({ code: 'organization_scope_missing', transport: 'fetch', path: telemetryPath });
    throw new SessionTransitionError();
  }

  const organizationSent = headers.get(ORGANIZATION_HEADER) ?? undefined;
  const shouldCaptureOrganization =
    Boolean(organizationSent) &&
    (scope?.kind === 'organization' || (!scope && (!hasCommittedOrganizationIdentity() || organizationSent === getCommittedOrganization())));
  const originOrganization = shouldCaptureOrganization ? organizationSent : undefined;
  const originOrganizationGeneration = shouldCaptureOrganization ? getOrganizationGeneration() : undefined;
  const originOrganizationSignal = shouldCaptureOrganization ? getOrganizationSignal() : undefined;
  const originOrganizationWasCommitted = shouldCaptureOrganization ? hasCommittedOrganizationIdentity() : false;

  const isActorCurrent = (): boolean =>
    authPolicy === 'anonymous' ||
    (dispatchSubject === getSessionSubject() && dispatchAuthGeneration === getAuthGeneration() && !dispatchAuthSignal.aborted);
  const isImplicitScopeCurrent = (): boolean =>
    !originOrganizationSignal ||
    (!originOrganizationSignal.aborted &&
      originOrganizationGeneration === getOrganizationGeneration() &&
      (!originOrganizationWasCommitted || originOrganization === getCommittedOrganization()));

  const requestMethod = inputRequest?.method;
  const method = (init.method ?? requestMethod ?? 'GET').toUpperCase();
  const semanticRead = !options.semanticWrite && (Boolean(options._sessionSemanticRead) || method === 'GET' || method === 'HEAD');
  let transportEntered = false;
  const isCurrent = (): boolean => {
    if (!isActorCurrent()) return false;
    if (options.operationRequirement) {
      return isOperationRequirementCurrent(options.operationRequirement, { transportEntered, semanticRead });
    }
    return isImplicitScopeCurrent();
  };

  // Mirrors the Axios transport's scope/requirement agreement guard: outside
  // a declared cross-target command, an explicit organization scope must
  // match the operation requirement's organization when one is present, or
  // the committed organization when none is.
  const isAdminTarget = isAdminTargetCapability(options._sessionAdminTargetCapability);
  if (options._sessionAdminTargetCapability !== undefined && !isAdminTarget) {
    emitSessionTelemetry({ code: 'organization_scope_mismatch', transport: 'fetch', path: telemetryPath });
    throw new SessionTransitionError();
  }
  if (scope?.kind === 'organization' && !isAdminTarget) {
    const requirementOrganization = options.operationRequirement?.context.organization;
    if (requirementOrganization !== undefined && organizationSent !== requirementOrganization) {
      emitSessionTelemetry({ code: 'organization_scope_mismatch', transport: 'fetch', path: telemetryPath });
      throw new SessionTransitionError();
    }
    if (!options.operationRequirement && originOrganizationWasCommitted && organizationSent !== getCommittedOrganization()) {
      emitSessionTelemetry({ code: 'organization_scope_mismatch', transport: 'fetch', path: telemetryPath });
      throw new SessionTransitionError();
    }
  }
  if (isAdminTarget && !options.operationRequirement) throw new SessionTransitionError();
  if (!isCurrent()) throw new SessionTransitionError();

  const callerSignals = [inputRequest?.signal, init.signal, options.signal].filter((candidate): candidate is AbortSignal => Boolean(candidate));
  if (!semanticRead && callerSignals.length > 0) {
    emitSessionTelemetry({ code: 'write_signal_rejected', transport: 'fetch', path: telemetryPath });
    throw new TypeError('Durable writes do not accept caller AbortSignal values');
  }
  const lifecycleSignal = semanticRead
    ? options.operationRequirement
      ? operationRequirementSignal(options.operationRequirement)
      : combineSignals([originOrganizationSignal, authPolicy === 'session' ? dispatchAuthSignal : undefined])
    : undefined;
  const signal = combineSignals([...callerSignals, lifecycleSignal]);
  const guard: SessionResponseGuard = {
    isCurrent,
    callerSignals,
    sessionSignal: lifecycleSignal,
    telemetryPath,
    stalePublicationTelemetryEmitted: false,
  };

  try {
    // No liveness signal is attached to semantic writes: after the call
    // enters fetch, an organization switch must not hide an uncertain write.
    const pendingResponse = fetch(input, { ...init, headers, signal });
    transportEntered = true;
    if (options.operationRequirement) markOperationRequirementTransportEntered(options.operationRequirement);
    const response = await pendingResponse;
    assertGuardCurrent(guard);
    return wrapBodyReaders(response, guard);
  } catch (error) {
    if (callerSignals.some((callerSignal) => callerSignal.aborted) && isAbortError(error)) throw error;
    if (error instanceof SessionTransitionError) throw error;
    if (lifecycleSignal?.aborted && isAbortError(error)) throw new SessionTransitionError();
    if (transportEntered && !isCurrent()) throw stalePublicationTransition(guard);
    throw error;
  }
};
